import { CONDITION_DEFAULT_PATH, validateConditionConfig } from "./conditionValidation";
import {
  DECISION_TABLE_NODE_TYPE,
  DECISION_TABLE_PATHS,
  validateDecisionTableConfig,
} from "./decisionTableValidation";
import {
  BROWSE_CATALOGUE_NODE_TYPE,
  BROWSE_CATALOGUE_PATHS,
  validateBrowseCatalogueConfig,
} from "./browseCatalogueValidation";
import {
  SEARCH_CATALOGUE_NODE_TYPE,
  SEARCH_CATALOGUE_PATHS,
  validateSearchCatalogueConfig,
} from "./searchCatalogueValidation";
import {
  PRESENT_CATALOGUE_RESULTS_NODE_TYPE,
  PRESENT_CATALOGUE_RESULTS_PATHS,
  validatePresentCatalogueResultsConfig,
} from "./presentCatalogueResultsValidation";
import {
  REVIEW_AND_SUBMIT_NODE_TYPE,
  REVIEW_AND_SUBMIT_PATHS,
  validateReviewAndSubmitConfig,
} from "./reviewAndSubmitValidation";
import {
  PREPARE_COMMERCE_SERVICE_NODE_TYPE,
  PREPARE_COMMERCE_SERVICE_OUTPUTS,
  validatePrepareCommerceServiceConfig,
} from "./prepareCommerceServiceValidation";
import {
  WHATSAPP_FLOW_NODE_TYPE,
  WHATSAPP_FLOW_OUTPUTS,
  validateWhatsAppFlowConfig,
} from "./whatsAppFlowValidation";
import {
  DYNAMIC_WHATS_FLOW_NODE_TYPE,
  DYNAMIC_WHATS_FLOW_OUTPUTS,
  validateDynamicWhatsFlowConfig,
} from "./dynamicWhatsFlowValidation";
import {
  SCHEDULING_BOOKING_NODE_TYPE,
  SCHEDULING_BOOKING_OUTPUTS,
  validateSchedulingBookingConfig,
} from "./schedulingBookingValidation";
import {
  CONFIRM_BOOKING_NODE_TYPE,
  CONFIRM_BOOKING_OUTPUTS,
  validateConfirmBookingConfig,
} from "./confirmBookingValidation";
import {
  URL_CTA_AFTER_SEND,
  URL_CTA_NODE_TYPE,
  validateUrlCtaConfig,
} from "./urlCtaValidation";

export const canonicalizeAutoReplyNodeType = rawType => {
  const normalized = String(rawType || "").trim().toLowerCase();
  if (normalized === "choice") return "button_choice";
  if (normalized === "condition") return "branch";
  return normalized;
};

const normalizeHandle = handle => String(handle || "").trim().toLowerCase();
const isChoiceHandle = handle => /^opt-\d+$/i.test(String(handle || "").trim());
const isTemplateHandle = handle =>
  /^button-\d+$/i.test(String(handle || "").trim());
const ADD_FLOW_NODE_TYPE = "add_flow";
const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const getNodeLabel = node =>
  String(node?.data?.label || node?.id || "Node").trim() || "Node";

const isTemplateBranching = (node, edges) =>
  canonicalizeAutoReplyNodeType(node?.type) === "template" &&
  edges.some(
    edge =>
      edge?.sourceNodeId === node?.id && isTemplateHandle(edge?.sourceHandle)
  );

const isBranchingNode = (node, edges) => {
  const nodeType = canonicalizeAutoReplyNodeType(node?.type);
  return nodeType === "button_choice" ||
    nodeType === "branch" ||
    nodeType === DECISION_TABLE_NODE_TYPE ||
    nodeType === BROWSE_CATALOGUE_NODE_TYPE ||
    nodeType === SEARCH_CATALOGUE_NODE_TYPE ||
    nodeType === PRESENT_CATALOGUE_RESULTS_NODE_TYPE ||
    nodeType === PREPARE_COMMERCE_SERVICE_NODE_TYPE ||
    nodeType === REVIEW_AND_SUBMIT_NODE_TYPE ||
    nodeType === WHATSAPP_FLOW_NODE_TYPE ||
    nodeType === DYNAMIC_WHATS_FLOW_NODE_TYPE ||
    nodeType === SCHEDULING_BOOKING_NODE_TYPE ||
    isTemplateBranching(node, edges);
};

const isExistingReturnEndpoint = (node, edges) => {
  const nodeType = canonicalizeAutoReplyNodeType(node?.type);
  return nodeType === "button_choice" || isTemplateBranching(node, edges);
};

const buildAdjacencyMap = edges => {
  const adjacency = new Map();

  edges.forEach(edge => {
    if (!adjacency.has(edge.sourceNodeId)) adjacency.set(edge.sourceNodeId, []);
    adjacency.get(edge.sourceNodeId).push(edge.targetNodeId);
  });

  return adjacency;
};

const canReachNode = (startNodeId, targetNodeId, adjacency) => {
  if (!startNodeId || !targetNodeId) return false;
  if (startNodeId === targetNodeId) return true;

  const queue = [startNodeId];
  const visited = new Set();

  while (queue.length > 0) {
    const currentNodeId = queue.shift();
    if (!currentNodeId || visited.has(currentNodeId)) continue;
    if (currentNodeId === targetNodeId) return true;

    visited.add(currentNodeId);
    const nextNodeIds = adjacency.get(currentNodeId) || [];
    nextNodeIds.forEach(nextNodeId => {
      if (!visited.has(nextNodeId)) queue.push(nextNodeId);
    });
  }

  return false;
};

const validatePayableBookingPaths = (nodes, edges, startNodeId) => {
  const nodeById = new Map(nodes.map(node => [String(node.id), node]));
  const outgoing = new Map();
  edges.forEach(edge => {
    const source = String(edge.sourceNodeId || "");
    if (!outgoing.has(source)) outgoing.set(source, []);
    outgoing.get(source).push(edge);
  });
  const queue = [{ nodeId: String(startNodeId), held: false, producer: "none", finalized: false }];
  const visited = new Set();

  while (queue.length) {
    const state = queue.shift();
    const stateKey = `${state.nodeId}|${state.held}|${state.producer}|${state.finalized}`;
    if (visited.has(stateKey)) continue;
    visited.add(stateKey);
    const node = nodeById.get(state.nodeId);
    if (!node) continue;
    const type = canonicalizeAutoReplyNodeType(node.type);
    if (type === CONFIRM_BOOKING_NODE_TYPE && node?.data?.config?.createPayableOrder === true) {
      if (!state.held || state.finalized !== true || !["prepare_booking", "present_booking"].includes(state.producer)) {
        return {
          ok: false,
          nodeId: node.id,
          message: state.producer === "ambiguous"
            ? "Payable Confirm Booking has conflicting Commerce producers on an executable path."
            : "Every path into a payable Confirm Booking must pass through Schedule Booking Held, one compatible Booking Commerce producer, and Review & Submit Submitted.",
        };
      }
    }

    for (const edge of outgoing.get(state.nodeId) || []) {
      const handle = normalizeHandle(edge.sourceHandle);
      let next = { ...state, nodeId: String(edge.targetNodeId) };
      if (type === SCHEDULING_BOOKING_NODE_TYPE) {
        next.held = handle === "held";
        next.producer = "none";
        next.finalized = false;
      }
      if (type === PREPARE_COMMERCE_SERVICE_NODE_TYPE && handle === "prepared") {
        const incomingProducer = next.held ? "prepare_booking" : "invalid";
        next.producer = next.producer === "none" ? incomingProducer : "ambiguous";
        next.finalized = false;
      }
      if (type === PRESENT_CATALOGUE_RESULTS_NODE_TYPE && handle === "selected" && node?.data?.config?.commerce) {
        const incomingProducer = String(node.data.config.commerce.transactionType || "").toLowerCase() === "booking"
          ? "present_booking" : "invalid";
        next.producer = next.producer === "none" ? incomingProducer : "ambiguous";
        next.finalized = false;
      }
      if (type === REVIEW_AND_SUBMIT_NODE_TYPE && handle === "submitted") {
        next.finalized = ["prepare_booking", "present_booking"].includes(next.producer);
      }
      queue.push(next);
    }
  }
  return { ok: true };
};

const validateChoiceEdges = (node, outgoingEdges) => {
  const options = Array.isArray(node?.data?.config?.options)
    ? node.data.config.options
    : [];
  const nodeLabel = getNodeLabel(node);

  if (options.length < 1) {
    return {
      ok: false,
      message: "Choice node must have at least 1 option.",
      nodeId: node.id,
    };
  }

  if (outgoingEdges.length === 0) {
    return {
      ok: false,
      message: `${nodeLabel} must connect each option to a next step.`,
      nodeId: node.id,
    };
  }

  const handles = outgoingEdges.map(edge => normalizeHandle(edge?.sourceHandle));
  const usesHandleMode = handles.some(Boolean);

  if (!usesHandleMode) {
    if (outgoingEdges.length !== options.length) {
      return {
        ok: false,
        message: `${nodeLabel} must connect every option to a next step. Expected ${options.length} connections but found ${outgoingEdges.length}.`,
        nodeId: node.id,
      };
    }
    return { ok: true };
  }

  if (handles.some(handle => !isChoiceHandle(handle))) {
    return {
      ok: false,
      message: `${nodeLabel} has an invalid option handle. Every choice connection must use opt-N, such as opt-0.`,
      nodeId: node.id,
    };
  }

  const duplicateHandle = handles.find(
    (handle, index) => handles.indexOf(handle) !== index
  );
  if (duplicateHandle) {
    return {
      ok: false,
      message: `${nodeLabel} has more than one connection for ${duplicateHandle}. Keep exactly one target for each option.`,
      nodeId: node.id,
    };
  }

  const indexes = handles.map(handle => Number(handle.slice("opt-".length)));
  const outOfRangeIndex = indexes.find(
    index => index < 0 || index >= options.length
  );
  if (outOfRangeIndex !== undefined) {
    return {
      ok: false,
      message: `${nodeLabel} has out-of-range handle opt-${outOfRangeIndex}. This choice has ${options.length} option(s).`,
      nodeId: node.id,
    };
  }

  const handleSet = new Set(handles);
  const missingIndex = options.findIndex(
    (_option, index) => !handleSet.has(`opt-${index}`)
  );
  if (missingIndex >= 0) {
    return {
      ok: false,
      message: `${nodeLabel} is missing a connection for option ${missingIndex + 1} (opt-${missingIndex}).`,
      nodeId: node.id,
    };
  }

  return { ok: true };
};

const validateTemplateEdges = (node, outgoingEdges) => {
  const buttonEdges = outgoingEdges.filter(edge =>
    isTemplateHandle(edge?.sourceHandle)
  );

  if (buttonEdges.length === 0) return { ok: true };

  if (buttonEdges.length !== outgoingEdges.length) {
    return {
      ok: false,
      message:
        "Interactive template connections must all use button-N handles.",
      nodeId: node.id,
    };
  }

  const handles = buttonEdges.map(edge => normalizeHandle(edge.sourceHandle));
  const duplicateHandle = handles.find(
    (handle, index) => handles.indexOf(handle) !== index
  );
  if (duplicateHandle) {
    return {
      ok: false,
      message: `Template node has more than one connection for ${duplicateHandle}.`,
      nodeId: node.id,
    };
  }

  return { ok: true };
};

export const validateAutoReplyConnection = (nodesArg, edgesArg, connection) => {
  const nodes = Array.isArray(nodesArg) ? nodesArg : [];
  const edges = Array.isArray(edgesArg) ? edgesArg : [];
  const sourceNode = nodes.find(node => node?.id === connection?.source);
  const targetNode = nodes.find(node => node?.id === connection?.target);

  if (!sourceNode || !targetNode) {
    return {
      ok: false,
      message: "Connection source and target must both exist in the flow.",
    };
  }

  const sourceType = canonicalizeAutoReplyNodeType(sourceNode.type);
  const targetType = canonicalizeAutoReplyNodeType(targetNode.type);
  const handle = normalizeHandle(connection?.sourceHandle);
  const targetHandle = normalizeHandle(connection?.targetHandle);
  const outgoingEdges = edges.filter(edge => edge?.source === sourceNode.id);

  if (sourceType === ADD_FLOW_NODE_TYPE) {
    return {
      ok: false,
      message: "Add Flow is terminal and cannot have an outgoing connection.",
    };
  }

  if (sourceType === "handover_agent") {
    return {
      ok: false,
      message: "Handover Agent is terminal and cannot have an outgoing connection.",
    };
  }

  if (
    targetType === ADD_FLOW_NODE_TYPE &&
    (sourceType !== "button_choice" || !isChoiceHandle(handle))
  ) {
    return {
      ok: false,
      message: "Add Flow can only be connected from a Choice option.",
    };
  }

  if (sourceType === URL_CTA_NODE_TYPE) {
    const config = sourceNode?.data?.config || {};
    const configResult = validateUrlCtaConfig(config);
    if (!configResult.isValid) {
      return { ok: false, message: configResult.message };
    }
    if (config.afterSend === URL_CTA_AFTER_SEND.END) {
      return {
        ok: false,
        message: "URL Node in End mode cannot have an outgoing connection.",
      };
    }
    if (handle) {
      return {
        ok: false,
        message: "URL Node uses one ordinary outgoing connection without a branch handle.",
      };
    }
    if (targetHandle) {
      return {
        ok: false,
        message: "URL Node must connect to an ordinary target without a branch handle.",
      };
    }
    if (outgoingEdges.length >= 1) {
      return {
        ok: false,
        message: "URL Node in Continue mode permits exactly one outgoing connection.",
      };
    }
  } else if (sourceType === "button_choice") {
    const optionCount = Array.isArray(sourceNode?.data?.config?.options)
      ? sourceNode.data.config.options.length
      : 0;

    if (!isChoiceHandle(handle)) {
      return {
        ok: false,
        message: "Choice connections must start from an opt-N option handle.",
      };
    }

    const index = Number(handle.slice("opt-".length));
    if (index < 0 || index >= optionCount) {
      return {
        ok: false,
        message: `${handle} is out of range for this ${optionCount}-option choice.`,
      };
    }
  } else if (sourceType === "branch") {
    const configResult = validateConditionConfig(sourceNode?.data?.config || {});
    if (!configResult.isValid) {
      return { ok: false, message: configResult.message };
    }

    const validHandles = new Set([
      ...sourceNode.data.config.rules.map(rule => rule.pathId),
      CONDITION_DEFAULT_PATH,
    ]);
    if (!handle || !validHandles.has(handle)) {
      return {
        ok: false,
        message: "Condition connections must start from a configured rule or default handle.",
      };
    }
  } else if (sourceType === DECISION_TABLE_NODE_TYPE) {
    const configResult = validateDecisionTableConfig(sourceNode?.data?.config || {});
    if (!configResult.isValid) return { ok: false, message: configResult.message };
    const validHandles = new Set(DECISION_TABLE_PATHS.map(path => path.id));
    if (!handle || !validHandles.has(handle)) {
      return {
        ok: false,
        message: "Decision Table connections must use Matched or No match.",
      };
    }
  } else if (sourceType === BROWSE_CATALOGUE_NODE_TYPE) {
    const configResult = validateBrowseCatalogueConfig(
      sourceNode?.data?.config || {}
    );
    if (!configResult.isValid) {
      return { ok: false, message: configResult.message };
    }
    const validHandles = new Set(
      BROWSE_CATALOGUE_PATHS.map(path => path.id)
    );
    if (!handle || !validHandles.has(handle)) {
      return {
        ok: false,
        message:
          "Browse Catalogue connections must start from Selected or No Categories.",
      };
    }
  } else if (sourceType === SEARCH_CATALOGUE_NODE_TYPE) {
    const configResult = validateSearchCatalogueConfig(
      sourceNode?.data?.config || {},
      nodes,
      edges,
      sourceNode.id
    );
    if (!configResult.isValid) {
      return { ok: false, message: configResult.message };
    }
    const validHandles = new Set(SEARCH_CATALOGUE_PATHS.map(path => path.id));
    if (!handle || !validHandles.has(handle)) {
      return {
        ok: false,
        message: "Search Catalogue connections must start from a result path.",
      };
    }
  } else if (sourceType === PRESENT_CATALOGUE_RESULTS_NODE_TYPE) {
    const configResult = validatePresentCatalogueResultsConfig(
      sourceNode?.data?.config || {},
      nodes
    );
    if (!configResult.isValid) {
      return { ok: false, message: configResult.message };
    }
    const validHandles = new Set(
      PRESENT_CATALOGUE_RESULTS_PATHS.map(path => path.id)
    );
    if (!handle || !validHandles.has(handle)) {
      return {
        ok: false,
        message:
          "Present Catalogue Results connections must start from Selected or Unavailable.",
      };
    }
  } else if (sourceType === PREPARE_COMMERCE_SERVICE_NODE_TYPE) {
    const configResult = validatePrepareCommerceServiceConfig(
      sourceNode?.data?.config || {},
      sourceNode?.data || {},
    );
    if (!configResult.isValid) return { ok: false, message: configResult.message };
    const validHandles = new Set(PREPARE_COMMERCE_SERVICE_OUTPUTS.map(path => path.id));
    if (!handle || !validHandles.has(handle)) {
      return { ok: false, message: "Prepare Commerce Service connections must use Prepared or Processing failed." };
    }
  } else if (sourceType === REVIEW_AND_SUBMIT_NODE_TYPE) {
    const configResult = validateReviewAndSubmitConfig(
      sourceNode?.data?.config || {},
      nodes,
      edges,
      sourceNode.id,
      { requireCommerceSource: false }
    );
    if (!configResult.isValid) {
      return { ok: false, message: configResult.message };
    }
    const validHandles = new Set(
      REVIEW_AND_SUBMIT_PATHS.map(path => path.id)
    );
    if (!handle || !validHandles.has(handle)) {
      return {
        ok: false,
        message:
          "Review & Submit connections must start from Submitted, Change Requested, or Cancelled.",
      };
    }
  } else if (sourceType === WHATSAPP_FLOW_NODE_TYPE) {
    const configResult = validateWhatsAppFlowConfig(sourceNode?.data?.config || {});
    if (!configResult.isValid) return { ok: false, message: configResult.message };
    const validHandles = new Set(WHATSAPP_FLOW_OUTPUTS.map(path => path.id));
    if (!handle || !validHandles.has(handle)) {
      return {
        ok: false,
        message: "Whats Form connections must start from one of its four configured paths.",
      };
    }
  } else if (sourceType === DYNAMIC_WHATS_FLOW_NODE_TYPE) {
    const configResult = validateDynamicWhatsFlowConfig(sourceNode?.data?.config || {});
    if (!configResult.isValid) return { ok: false, message: configResult.message };
    const validHandles = new Set(DYNAMIC_WHATS_FLOW_OUTPUTS.map(path => path.id));
    if (!handle || !validHandles.has(handle)) {
      return { ok: false, message: "Dynamic Whats Form connections must use Sent or Send failed." };
    }
  } else if (sourceType === SCHEDULING_BOOKING_NODE_TYPE) {
    const configResult = validateSchedulingBookingConfig(sourceNode?.data?.config || {});
    if (!configResult.isValid) return { ok: false, message: configResult.message };
    const validHandles = new Set(SCHEDULING_BOOKING_OUTPUTS.map(path => path.id));
    if (!handle || !validHandles.has(handle)) return { ok: false, message: "Schedule Booking connections must use one of its four fixed paths." };
  } else if (sourceType === CONFIRM_BOOKING_NODE_TYPE) {
    const configResult = validateConfirmBookingConfig(sourceNode?.data?.config || {});
    if (!configResult.isValid) return { ok: false, message: configResult.message };
    const validHandles = new Set(CONFIRM_BOOKING_OUTPUTS.map(path => path.id));
    if (!handle || !validHandles.has(handle)) return { ok: false, message: "Confirm Booking connections must use one of its three fixed paths." };
  } else if (sourceType === "template" && handle) {
    if (!isTemplateHandle(handle)) {
      return {
        ok: false,
        message: "Interactive template connections must use button-N handles.",
      };
    }
  } else if (outgoingEdges.length >= 1) {
    return {
      ok: false,
      message: `${getNodeLabel(sourceNode)} can have only one outgoing connection.`,
    };
  }

  if (
    handle &&
    outgoingEdges.some(edge => normalizeHandle(edge?.sourceHandle) === handle)
  ) {
    return {
      ok: false,
      message: `${handle} already has a target. Remove the existing connection first.`,
    };
  }

  if (
    sourceType === "template" &&
    outgoingEdges.length > 0 &&
    (!handle ||
      outgoingEdges.some(edge => !isTemplateHandle(edge?.sourceHandle)))
  ) {
    return {
      ok: false,
      message:
        "A template cannot mix a default connection with button branch connections.",
    };
  }

  return { ok: true };
};

export const validateAutoReplyGraph = (nodesArg, edgesArg) => {
  const normalizedNodes = Array.isArray(nodesArg) ? nodesArg : [];
  const normalizedEdges = Array.isArray(edgesArg) ? edgesArg : [];
  const nodeIds = normalizedNodes.map(node => node?.id).filter(Boolean);
  const nodeById = new Map(normalizedNodes.filter(node => node?.id).map(node => [node.id, node]));

  if (nodeById.size === 0) {
    return { ok: false, message: "Flow must contain at least one node." };
  }

  if (nodeIds.length !== normalizedNodes.length) {
    return { ok: false, message: "Every flow node must have a valid id." };
  }

  if (new Set(nodeIds).size !== nodeIds.length) {
    return { ok: false, message: "Flow contains duplicate node ids." };
  }

  const startNodes = normalizedNodes.filter(
    node => canonicalizeAutoReplyNodeType(node?.type) === "start"
  );
  if (startNodes.length !== 1) {
    return {
      ok: false,
      message:
        startNodes.length === 0
          ? "Flow must contain exactly one Start node."
          : "Flow cannot contain more than one Start node.",
    };
  }

  if (!normalizedEdges.some(edge => edge?.sourceNodeId === startNodes[0].id)) {
    return {
      ok: false,
      message: "Flow must have at least 1 edge starting from Start node.",
      nodeId: startNodes[0].id,
    };
  }

  for (const edge of normalizedEdges) {
    if (!edge?.sourceNodeId || !edge?.targetNodeId) {
      return {
        ok: false,
        message: "Each connection must include both source and target nodes.",
      };
    }
    if (!nodeById.has(edge.sourceNodeId) || !nodeById.has(edge.targetNodeId)) {
      return {
        ok: false,
        message: "A connection references a node that no longer exists.",
      };
    }
  }

  for (const node of normalizedNodes) {
    const outgoingEdges = normalizedEdges.filter(
      edge => edge?.sourceNodeId === node.id
    );
    const incomingEdges = normalizedEdges.filter(
      edge => edge?.targetNodeId === node.id
    );
    const nodeType = canonicalizeAutoReplyNodeType(node?.type);

    if (nodeType === ADD_FLOW_NODE_TYPE) {
      const config = node?.data?.config || {};
      const targetFlowId = String(config?.targetFlowId || "").trim();
      if (config?.schemaVersion !== 1 || !GUID_PATTERN.test(targetFlowId)) {
        return {
          ok: false,
          message: "Add Flow must select an available target flow.",
          nodeId: node.id,
        };
      }
      if (outgoingEdges.length > 0) {
        return {
          ok: false,
          message: "Add Flow is terminal and cannot have an outgoing connection.",
          nodeId: node.id,
        };
      }
      if (incomingEdges.length === 0) {
        return {
          ok: false,
          message: "Add Flow must be connected from a Choice option.",
          nodeId: node.id,
        };
      }
      const invalidPredecessor = incomingEdges.find(edge => {
        const sourceNode = nodeById.get(edge.sourceNodeId);
        return (
          canonicalizeAutoReplyNodeType(sourceNode?.type) !== "button_choice" ||
          !isChoiceHandle(edge?.sourceHandle)
        );
      });
      if (invalidPredecessor) {
        return {
          ok: false,
          message: "Add Flow can only be reached from a Choice option.",
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === "handover_agent") {
      if (outgoingEdges.length > 0) {
        return {
          ok: false,
          message: "Handover Agent is terminal and cannot have an outgoing connection.",
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === URL_CTA_NODE_TYPE) {
      const config = node?.data?.config || {};
      const configResult = validateUrlCtaConfig(config);
      if (!configResult.isValid) {
        return {
          ok: false,
          message: `URL Node: ${configResult.message}`,
          nodeId: node.id,
        };
      }
      if (config.afterSend === URL_CTA_AFTER_SEND.CONTINUE) {
        if (outgoingEdges.length !== 1) {
          return {
            ok: false,
            message: "URL Node in Continue mode requires exactly one outgoing connection.",
            nodeId: node.id,
          };
        }
        if (normalizeHandle(outgoingEdges[0]?.sourceHandle)) {
          return {
            ok: false,
            message: "URL Node uses one ordinary outgoing connection without a branch handle.",
            nodeId: node.id,
          };
        }
      } else if (outgoingEdges.length > 0) {
        return {
          ok: false,
          message: "URL Node in End mode cannot have an outgoing connection.",
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === "input_capture" &&
        (outgoingEdges.length !== 1 || String(outgoingEdges[0]?.sourceHandle || "").trim())) {
      return {
        ok: false,
        message: `${getNodeLabel(node)} must have exactly one default outgoing connection.`,
        nodeId: node.id,
      };
    }

    if (nodeType === "branch") {
      const config = node?.data?.config || {};
      const configResult = validateConditionConfig(config);
      if (!configResult.isValid) return { ok: false, message: configResult.message, nodeId: node.id };
      const expected = new Set([
        ...config.rules.map(rule => rule.pathId),
        CONDITION_DEFAULT_PATH,
      ]);
      const handles = outgoingEdges.map(edge => String(edge?.sourceHandle || "").trim());
      if (
        handles.some(handle => !handle) ||
        handles.length !== expected.size ||
        new Set(handles).size !== handles.length ||
        handles.some(handle => !expected.has(handle))
      ) {
        return {
          ok: false,
          message: `${getNodeLabel(node)} must connect every rule and the default path exactly once.`,
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === DECISION_TABLE_NODE_TYPE) {
      const configResult = validateDecisionTableConfig(node?.data?.config || {});
      if (!configResult.isValid) return { ok: false, message: configResult.message, nodeId: node.id };
      const expected = new Set(DECISION_TABLE_PATHS.map(path => path.id));
      const handles = outgoingEdges.map(edge => String(edge?.sourceHandle || "").trim());
      if (
        handles.some(handle => !handle) ||
        handles.length !== expected.size ||
        new Set(handles).size !== handles.length ||
        handles.some(handle => !expected.has(handle))
      ) {
        return {
          ok: false,
          message: `${getNodeLabel(node)} must connect Matched and No match exactly once.`,
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === BROWSE_CATALOGUE_NODE_TYPE) {
      const configResult = validateBrowseCatalogueConfig(
        node?.data?.config || {}
      );
      if (!configResult.isValid) {
        return {
          ok: false,
          message: configResult.message,
          nodeId: node.id,
        };
      }
      const expected = new Set(
        BROWSE_CATALOGUE_PATHS.map(path => path.id)
      );
      const handles = outgoingEdges.map(edge =>
        String(edge?.sourceHandle || "").trim()
      );
      if (
        handles.some(handle => !handle) ||
        handles.length !== expected.size ||
        new Set(handles).size !== handles.length ||
        handles.some(handle => !expected.has(handle))
      ) {
        return {
          ok: false,
          message: `${getNodeLabel(node)} must connect Selected and No Categories exactly once.`,
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === SEARCH_CATALOGUE_NODE_TYPE) {
      const configResult = validateSearchCatalogueConfig(
        node?.data?.config || {},
        normalizedNodes,
        normalizedEdges,
        node.id
      );
      if (!configResult.isValid) {
        return { ok: false, message: configResult.message, nodeId: node.id };
      }
      const expected = new Set(SEARCH_CATALOGUE_PATHS.map(path => path.id));
      const handles = outgoingEdges.map(edge =>
        String(edge?.sourceHandle || "").trim()
      );
      if (
        handles.some(handle => !handle) ||
        handles.length !== expected.size ||
        new Set(handles).size !== handles.length ||
        handles.some(handle => !expected.has(handle))
      ) {
        return {
          ok: false,
          message: `${getNodeLabel(node)} must connect No Results, Single Result, and Multiple Results exactly once.`,
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === PRESENT_CATALOGUE_RESULTS_NODE_TYPE) {
      const configResult = validatePresentCatalogueResultsConfig(
        node?.data?.config || {},
        normalizedNodes
      );
      if (!configResult.isValid) {
        return {
          ok: false,
          message: configResult.message,
          nodeId: node.id,
        };
      }
      const expected = new Set(
        PRESENT_CATALOGUE_RESULTS_PATHS.map(path => path.id)
      );
      const handles = outgoingEdges.map(edge =>
        String(edge?.sourceHandle || "").trim()
      );
      if (
        handles.some(handle => !handle) ||
        handles.length !== expected.size ||
        new Set(handles).size !== handles.length ||
        handles.some(handle => !expected.has(handle))
      ) {
        return {
          ok: false,
          message: `${getNodeLabel(node)} must connect Selected and Unavailable exactly once.`,
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === PREPARE_COMMERCE_SERVICE_NODE_TYPE) {
      const configResult = validatePrepareCommerceServiceConfig(
        node?.data?.config || {},
        node?.data || {},
      );
      if (!configResult.isValid) return { ok: false, message: configResult.message, nodeId: node.id };
      const expected = new Set(PREPARE_COMMERCE_SERVICE_OUTPUTS.map(path => path.id));
      const handles = outgoingEdges.map(edge => normalizeHandle(edge?.sourceHandle));
      if (handles.some(handle => !handle) || handles.length !== expected.size ||
          new Set(handles).size !== handles.length || handles.some(handle => !expected.has(handle))) {
        return { ok: false, message: "Prepare Commerce Service needs exactly one Prepared and one Processing failed connection.", nodeId: node.id };
      }
      continue;
    }

    if (nodeType === REVIEW_AND_SUBMIT_NODE_TYPE) {
      const configResult = validateReviewAndSubmitConfig(
        node?.data?.config || {},
        normalizedNodes,
        normalizedEdges,
        node.id
      );
      if (!configResult.isValid) {
        return {
          ok: false,
          message: configResult.message,
          nodeId: node.id,
        };
      }
      const expected = new Set(
        REVIEW_AND_SUBMIT_PATHS.map(path => path.id)
      );
      const handles = outgoingEdges.map(edge =>
        String(edge?.sourceHandle || "").trim()
      );
      if (
        handles.some(handle => !handle) ||
        handles.length !== expected.size ||
        new Set(handles).size !== handles.length ||
        handles.some(handle => !expected.has(handle))
      ) {
        return {
          ok: false,
          message: `${getNodeLabel(node)} must connect Submitted, Change Requested, and Cancelled exactly once.`,
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === WHATSAPP_FLOW_NODE_TYPE) {
      const configResult = validateWhatsAppFlowConfig(node?.data?.config || {});
      if (!configResult.isValid) {
        return { ok: false, message: configResult.message, nodeId: node.id };
      }
      const expected = new Set(WHATSAPP_FLOW_OUTPUTS.map(path => path.id));
      const handles = outgoingEdges.map(edge =>
        String(edge?.sourceHandle || "").trim()
      );
      if (
        handles.some(handle => !handle) ||
        handles.length !== expected.size ||
        new Set(handles).size !== handles.length ||
        handles.some(handle => !expected.has(handle))
      ) {
        return {
          ok: false,
          message: `${getNodeLabel(node)} must connect Submitted, Send Failed, Timed Out, and Processing Failed exactly once.`,
          nodeId: node.id,
        };
      }
      continue;
    }

    if (nodeType === DYNAMIC_WHATS_FLOW_NODE_TYPE) {
      const configResult = validateDynamicWhatsFlowConfig(node?.data?.config || {});
      if (!configResult.isValid) return { ok: false, message: configResult.message, nodeId: node.id };
      const expected = new Set(DYNAMIC_WHATS_FLOW_OUTPUTS.map(path => path.id));
      const handles = outgoingEdges.map(edge => String(edge?.sourceHandle || "").trim());
      if (handles.some(handle => !handle) || handles.length !== expected.size
        || new Set(handles).size !== handles.length || handles.some(handle => !expected.has(handle))) {
        return { ok: false, message: "Dynamic Whats Form needs exactly one Sent and one Send failed connection.", nodeId: node.id };
      }
      continue;
    }

    if (nodeType === SCHEDULING_BOOKING_NODE_TYPE) {
      const configResult = validateSchedulingBookingConfig(node?.data?.config || {});
      if (!configResult.isValid) return { ok: false, message: configResult.message, nodeId: node.id };
      const expected = new Set(SCHEDULING_BOOKING_OUTPUTS.map(path => path.id));
      const handles = outgoingEdges.map(edge => String(edge?.sourceHandle || "").trim());
      if (handles.some(handle => !handle) || handles.length !== expected.size || new Set(handles).size !== handles.length
        || handles.some(handle => !expected.has(handle))) return { ok: false, message: "Schedule Booking needs exactly one Held, Send failed, Timed out, and Processing failed connection.", nodeId: node.id };
      continue;
    }

    if (nodeType === CONFIRM_BOOKING_NODE_TYPE) {
      const configResult = validateConfirmBookingConfig(node?.data?.config || {});
      if (!configResult.isValid) return { ok: false, message: configResult.message, nodeId: node.id };
      const expected = new Set(CONFIRM_BOOKING_OUTPUTS.map(path => path.id));
      const handles = outgoingEdges.map(edge => String(edge?.sourceHandle || "").trim());
      if (handles.some(handle => !handle) || handles.length !== expected.size || new Set(handles).size !== handles.length
        || handles.some(handle => !expected.has(handle))) return { ok: false, message: "Confirm Booking needs exactly one Confirmed, Hold expired, and Processing failed connection.", nodeId: node.id };
      continue;
    }

    if (nodeType === "button_choice") {
      const result = validateChoiceEdges(node, outgoingEdges);
      if (!result.ok) return result;
      continue;
    }

    if (nodeType === "template") {
      const result = validateTemplateEdges(node, outgoingEdges);
      if (!result.ok) return result;
    }

    if (!isBranchingNode(node, normalizedEdges) && outgoingEdges.length > 1) {
      return {
        ok: false,
        message: `${getNodeLabel(node)} can have only one outgoing connection.`,
        nodeId: node.id,
      };
    }
  }

  const approvedReturnEdges = new Set(
    normalizedEdges.filter(edge => {
      if (!edge?.sourceNodeId || !edge?.targetNodeId) return false;
      if (edge.sourceNodeId === edge.targetNodeId) return false;

      const sourceNode = nodeById.get(edge.sourceNodeId);
      const targetNode = nodeById.get(edge.targetNodeId);
      if (!sourceNode || !targetNode) {
        return false;
      }

      const existingInteractiveReturn =
        isExistingReturnEndpoint(sourceNode, normalizedEdges) &&
        isExistingReturnEndpoint(targetNode, normalizedEdges);
      const choiceToInputCaptureRetry =
        canonicalizeAutoReplyNodeType(sourceNode.type) === "button_choice" &&
        isChoiceHandle(edge.sourceHandle) &&
        canonicalizeAutoReplyNodeType(targetNode.type) === "input_capture";
      const reviewChangeToInputCapture =
        canonicalizeAutoReplyNodeType(sourceNode.type) ===
          REVIEW_AND_SUBMIT_NODE_TYPE &&
        REVIEW_AND_SUBMIT_PATHS.some(
          path =>
            path.id === "change_requested" &&
            normalizeHandle(edge.sourceHandle) === path.id
        ) &&
        canonicalizeAutoReplyNodeType(targetNode.type) === "input_capture";

      if (
        !existingInteractiveReturn &&
        !choiceToInputCaptureRetry &&
        !reviewChangeToInputCapture
      ) {
        return false;
      }

      const adjacencyWithoutCurrentEdge = buildAdjacencyMap(
        normalizedEdges.filter(candidate => candidate !== edge)
      );
      return canReachNode(
        edge.targetNodeId,
        edge.sourceNodeId,
        adjacencyWithoutCurrentEdge
      );
    })
  );

  const adjacency = buildAdjacencyMap(
    normalizedEdges.filter(edge => !approvedReturnEdges.has(edge))
  );
  const visitState = new Map();
  const detectCycle = nodeId => {
    const state = visitState.get(nodeId);
    if (state === 1) return true;
    if (state === 2) return false;

    visitState.set(nodeId, 1);
    for (const nextNodeId of adjacency.get(nodeId) || []) {
      if (detectCycle(nextNodeId)) return true;
    }
    visitState.set(nodeId, 2);
    return false;
  };

  for (const nodeId of nodeById.keys()) {
    if (detectCycle(nodeId)) {
      return {
        ok: false,
        message:
          "Flow can only loop from an interactive option back to an earlier menu or Input Capture node.",
        nodeId,
      };
    }
  }


  const payableResult = validatePayableBookingPaths(
    normalizedNodes,
    normalizedEdges,
    startNodes[0].id
  );
  if (!payableResult.ok) return payableResult;

  return { ok: true };
};
